import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import {
	ConfirmationDialog,
	FloatingActionButton,
	Pagination,
	SearchInput,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction
} from "client/Components";
import { SettingsPage } from "../SettingsPage";
import { contains } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useSelection,
	useSortHandler,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	deleteGateway,
	GatewayModel,
	listGateways
} from "client/Api/ClientlessRdp/clientlessRdp";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { EMPTY_ARRAY, resolveFormattedTextString } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter,
	route
} from "client/Navigation";
import { GatewayConfigurationParams } from "./GatewayConfiguration";
import {
	CreateConnectionConfigurationParams,
	EditConnectionConfigurationParams
} from "./ConnectionConfiguration";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

const {
	Row,
	Cell
} = TableView;

type GatewayActionContext = {
	selection: readonly GatewayModel[];
	isMobile: boolean;
};

type GatewayAction = ToolbarAction<GatewayActionContext>;

const useGatewayDataSource = createLocalDataSource<GatewayModel>()
	.setConfiguration({
		search: { doesMatchItem: searchMatchFunction },
		hasPagination: true
	})
	.getHook();

export type GatewayOverviewProps = {
	onNavigate: NavigationCallback;
};

export const GatewayOverview = (props: GatewayOverviewProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [items, setItems] = useState<readonly GatewayModel[]>(EMPTY_ARRAY);
	const [idToBeDeleted, setIdToBeDeleted] = useState<number | undefined>(undefined);
	const [isMakingRequest, setIsMakingRequest] = useState<boolean>(false);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<GatewayModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selectedItems, setSelectedItems] = useState<readonly GatewayModel[]>([]);

	const alertManager: AlertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.ClientlessRDP);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsMakingRequest(true);

					const items: GatewayModel[] = await listGateways(signal)
						.catch(handlePageDataLoadingError);

					setItems(items);
					setIsMakingRequest(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsMakingRequest(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const { sortingFor } = useSortHandler<GatewayModel>(sortingState, setSortingState);

	const { currentItems, pagination } = useGatewayDataSource(
		items, {
		sorting: sortingState,
		pagination: {
			currentPageNumber
		},
		searchTerm
	});

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const newGatewayHandler = useCallback(
		(): void => {
			onNavigate(clientlessRdpRouting.createGateway);
		},
		[onNavigate]
	);

	const actionContext = useMemo(
		(): GatewayActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	const selectionActions = useMemo(
		(): readonly GatewayAction[] => {
			return getSelectionActions({
				deselectHandler: () => setSelectedItems([])
			});
		},
		[]
	);

	const contextualActions = useMemo(
		(): readonly GatewayAction[] => {
			return listContextualActions({
				editGatewayHandler: (gatewayId) => onNavigate(
					clientlessRdpRouting.editGateway({gatewayId: `${gatewayId}`})
				),
				viewGatewayHandler: (gatewayId) => onNavigate(
					clientlessRdpRouting.gatewayDetails({gatewayId: `${gatewayId}`})
				),
				deleteHandler: setIdToBeDeleted
			});
		},
		[onNavigate]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<GatewayActionContext> => {
			return getMainAction(newGatewayHandler);
		},
		[newGatewayHandler]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly GatewayAction[] => {
			return buildActionsForTopBar<GatewayActionContext, GatewayAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	const deleteHandler = async (id: number): Promise<void> => {
		setIsMakingRequest(true);
		try {
			await deleteGateway(id);
			setItems(
				(x) => {
					const copy: GatewayModel[] = [...x];
					const index: number = copy.findIndex(x => x.id === id);
					copy.splice(index, 1);
					return copy;
				}
			);

			alertManager.showSuccess(localize("_GatewayDeletedSuccessfully"));
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_ErrorDuringGatewayDeletion"));
		} finally {
			setIdToBeDeleted(undefined);
			setIsMakingRequest(false);
		}
	};

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_ClientlessRDP")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_ClientlessRDP")
						}
					]
				})
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isMakingRequest}
				actions={topBarActions}
				actionContext={actionContext}
				hasSelection={hasSelection}
				title={localize("_Gateways")}
			>
				<SettingsPage.Section isVisible={items.length === 0}>
					<p>{localize("_GatewaySettingsEmpty")}</p>
				</SettingsPage.Section>

				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInput
							onChange={setSearchTerm}
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Name"),
										localize("_GatewayUrl")
									]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							items={currentItems}
							itemKey="id"
							height="stretch"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									trackingId: "name"
								},
								{
									title: localize("_GatewayUrl"),
									sortable: sortingFor("gatewayUrl"),
									trackingId: "gateway-url"
								}
							]}
							trackingId="gateway-overview"
							noResultConfig={noResultConfig}
						>
							{item => (
								<Row
									selectable={selectionFor(item)}
									trackingId={item.id.toString()}
								>
									<Cell>{item.name}</Cell>
									<Cell>{item.gatewayUrl}</Cell>
								</Row>
							)}
						</TableView>
						<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>

			{ idToBeDeleted && (
				<ConfirmationDialog
					title={localize("_DeleteGatewayConfirmationTitle")}
					confirmText={localize("_Delete")}
					onConfirmation={() => deleteHandler(idToBeDeleted)}
					onCancellation={() => setIdToBeDeleted(undefined)}
					trackingId="delete-crdp-gateway-confirmation-dialog"
				>
					{ localize("_DeleteGatewayConfirmationText")}
				</ConfirmationDialog>
			)}
		</>
	);
};
GatewayOverview.displayName = "GatewayOverview";

export const GatewayOverviewForRouter: FunctionComponent = connectToRouter(
	GatewayOverview,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

function searchMatchFunction(
	item: GatewayModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name,
		gatewayUrl
	} = item;

	return contains(name, normalizedNotEmptySearchTerm)
		|| contains(gatewayUrl, normalizedNotEmptySearchTerm);
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly GatewayAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function listContextualActions(
	handlers: {
		viewGatewayHandler: (gatewayId: number) => void;
		editGatewayHandler: (gatewayId: number) => void;
		deleteHandler: (id: number) => void;
	}
): readonly GatewayAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "open",
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => handlers.viewGatewayHandler(c.selection[0].id)
		},
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			icon: "pen",
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => handlers.editGatewayHandler(c.selection[0].id)
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => handlers.deleteHandler(c.selection[0].id)
		}
	];
}

function getMainAction(
	newGatewayHandler: () => void
): ToolbarRegularAction<GatewayActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		icon: "plus",
		canExecute: () => true,
		execute: () => newGatewayHandler(),
		isPrimary: true
	};
}

export const clientlessRdpRouting = {
	gatewayOverview: "/ClientlessRdp",
	createGateway: "/ClientlessRdp/Gateway/New",
	gatewayDetails: route<GatewayConfigurationParams>`/ClientlessRdp/Gateway/${"gatewayId"}`,
	editGateway: route<GatewayConfigurationParams>`/ClientlessRdp/Gateway/${"gatewayId"}/Edit`,
	createConnection: route<CreateConnectionConfigurationParams>`/ClientlessRdp/Gateway/${"gatewayId"}/Connection/New`,
	connectionDetails: route<EditConnectionConfigurationParams>`/ClientlessRdp/Gateway/${"gatewayId"}/Connection/${"connectionId"}`,
	editConnection: route<EditConnectionConfigurationParams>`/ClientlessRdp/Gateway/${"gatewayId"}/Connection/${"connectionId"}/Edit`
};